import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentServiceconnectionsSagehrDatabases } from '@/api/generated/company-management-user/company-management-user'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select'

interface Props {
  /** Current value of the connection's API base URL field — the list is per instance. */
  apiBaseUrl: string
  value: string
  onChange: (databaseKey: string) => void
}

/**
 * Picks a Sage HR database. The user knows the database by name, never by its key, so the
 * key is only ever chosen from the instance's own list. The picker is never disabled: a
 * stored key must stay visible even when the list cannot be loaded.
 */
export function SageHrDatabaseKeyPicker({ apiBaseUrl, value, onChange }: Props) {
  const { t } = useTranslation('service-connections')
  // The URL as it was when the picker was last opened. Querying the live field value would
  // call the external Sage instance on every keystroke in the URL input.
  const [queriedApiBaseUrl, setQueriedApiBaseUrl] = useState<string | null>(null)

  const trimmedApiBaseUrl = apiBaseUrl.trim()
  const hasApiBaseUrl = trimmedApiBaseUrl.length > 0

  const { data, isFetching, isError, refetch } =
    useGetApiCompaniesCurrentServiceconnectionsSagehrDatabases(
      { ApiBaseUrl: queriedApiBaseUrl ?? '' },
      { query: { enabled: !!queriedApiBaseUrl } },
    )

  // Every open retries: a changed URL starts its own query, the same URL refetches. So a
  // load that failed or never ran is always one click away from being tried again.
  const handleOpen = () => {
    if (!hasApiBaseUrl) return

    if (trimmedApiBaseUrl === queriedApiBaseUrl) {
      refetch()
    } else {
      setQueriedApiBaseUrl(trimmedApiBaseUrl)
    }
  }

  const loaded = useMemo(
    () =>
      (data?.data ?? [])
        .filter((database) => !!database.key)
        .map((database) => ({ key: database.key!, name: database.name })),
    [data],
  )

  // The stored key stays selectable while the list is unloaded, still loading, failed, or no
  // longer offers it — otherwise an existing connection would show an empty database field.
  const options = useMemo(
    () =>
      value && !loaded.some((database) => database.key === value)
        ? [{ key: value, name: null }, ...loaded]
        : loaded,
    [loaded, value],
  )

  // `queriedApiBaseUrl === null` means the first fetch has not started yet — reporting "no
  // databases" in that frame would be wrong, it is about to load.
  const statusMessage = !hasApiBaseUrl
    ? t('dialog.sageHr.databaseKey.needsApiBaseUrl')
    : isFetching || queriedApiBaseUrl === null
      ? t('dialog.sageHr.databaseKey.loading')
      : isError
        ? t('dialog.sageHr.databaseKey.loadError')
        : loaded.length === 0
          ? t('dialog.sageHr.databaseKey.none')
          : null

  return (
    <Select
      value={value || undefined}
      // Only a real pick may touch the form: never echo the current value back, never write
      // an empty one. Opening, loading and re-rendering leave the form state alone.
      onValueChange={(picked) => picked && picked !== value && onChange(picked)}
      onOpenChange={(open) => open && handleOpen()}
    >
      <SelectTrigger id="service-connection-database-key" className="w-full">
        <SelectValue placeholder={t('dialog.sageHr.databaseKey.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        {statusMessage && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{statusMessage}</div>
        )}
        {options.map((database) => (
          <SelectItem key={database.key} value={database.key}>
            {database.name ?? database.key}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
